import { apiFetch } from '../api/client';

export function ViewPastePage(id: string): void {
    const app = document.getElementById('app');
    if (!app) return;

    app.innerHTML = `
        <div class="container">
            <header class="header">
                <h1>Mediabin</h1>
                <a href="/" class="back-link">← New Paste</a>
            </header>
            <main class="main" id="paste-content">
                <div class="loading">Loading paste...</div>
            </main>
        </div>
    `;

    loadPaste(id);
}

async function loadPaste(id: string): Promise<void> {
    const content = document.getElementById('paste-content');
    if (!content) return;

    try {
        const hash = window.location.hash.slice(1);
        const headers: Record<string, string> = {};

        const params = new URLSearchParams(window.location.search);
        const share = params.get('share');

        const password = sessionStorage.getItem(`paste_password_${id}`);
        if (password) {
            headers['X-Paste-Password'] = password;
        }

        const query = share ? `?share=${encodeURIComponent(share)}` : '';
        const response = await fetch(`/api/paste/${id}${query}`, { headers });

        if (response.status === 403) {
            const err = await response.json().catch(() => ({ detail: '' }));
            if (err.detail && err.detail.includes('private paste')) {
                showLoginPrompt(id, content);
            } else {
                showPasswordPrompt(id, content);
            }
            return;
        }

        if (response.status === 410) {
            content.innerHTML = '<div class="error"><p>This paste has been burned and is no longer available.</p></div>';
            return;
        }

        if (!response.ok) {
            content.innerHTML = '<div class="error"><p>Paste not found or has expired.</p></div>';
            return;
        }

        const data = await response.json();
        renderPaste(data, content, hash);
    } catch (error) {
        content.innerHTML = `<div class="error"><p>Error loading paste: ${error instanceof Error ? error.message : 'Unknown error'}</p></div>`;
    }
}

function renderPaste(data: Record<string, unknown>, container: HTMLElement, encryptionKey: string): void {
    let contentHtml = '';
    let pasteContent = data.content as string | null;

    // Handle encrypted pastes
    if (data.is_encrypted && encryptionKey) {
        // Client-side decryption would happen here
        pasteContent = '[Encrypted content - decryption handled client-side]';
    }

    if (data.mode === 'text' || data.mode === 'text_media') {
        const lang = data.syntax_lang as string | undefined;
        const renderedHtml = data.rendered_html as string | undefined;
        if (lang === 'markdown' && renderedHtml) {
            // rendered_html is sanitized server-side by nh3 (markdown_service).
            contentHtml = `<div class="markdown-body">${renderedHtml}</div>`;
        } else if (lang) {
            contentHtml = `<pre class="code-block"><code class="language-${lang}">${numberedLines(pasteContent || '')}</code></pre>`;
        } else {
            contentHtml = `<pre class="text-block">${numberedLines(pasteContent || '')}</pre>`;
        }
    }

    // Render media if any
    let mediaHtml = '';
    const media = data.media as Array<Record<string, unknown>> | undefined;
    if (media && media.length > 0) {
        mediaHtml = '<div class="media-gallery">';
        for (const m of media) {
            mediaHtml += `<img src="${m.url as string}" alt="${m.mime_type as string}" loading="lazy">`;
        }
        mediaHtml += '</div>';
    }

    container.innerHTML = `
        <div class="paste-header">
            <div class="meta">
                <span class="mode-badge">${data.mode as string}</span>
                <span class="visibility-badge">${data.visibility as string}</span>
                ${data.expires_at ? `<span class="expiry">Expires: ${new Date(data.expires_at as string).toLocaleString()}</span>` : ''}
                <span class="views">Views: ${data.view_count as number}</span>
            </div>
            <div class="actions">
                <a href="/paste/${data.id as string}/raw${window.location.search}" class="btn btn-secondary">Raw</a>
                <button class="btn btn-secondary" onclick="navigator.clipboard.writeText(window.location.href)">Copy URL</button>
            </div>
        </div>
        ${contentHtml}
        ${mediaHtml}
    `;
}

function showPasswordPrompt(id: string, container: HTMLElement): void {
    container.innerHTML = `
        <div class="password-prompt">
            <h3>This paste is password protected</h3>
            <input type="password" id="password-input" placeholder="Enter password">
            <button class="btn btn-primary" id="password-submit-btn">Submit</button>
            <p class="error-msg" id="password-error" style="display: none;"></p>
        </div>
    `;

    const input = document.getElementById('password-input') as HTMLInputElement;
    const submitBtn = document.getElementById('password-submit-btn');

    const submitPassword = async () => {
        const password = input.value;
        if (!password) return;

        sessionStorage.setItem(`paste_password_${id}`, password);
        await loadPaste(id);
    };

    if (submitBtn) {
        submitBtn.addEventListener('click', submitPassword);
    }

    input?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') submitPassword();
    });
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function numberedLines(text: string): string {
    return escapeHtml(text.replace(/\r\n/g, '\n')).split('\n')
        .map(line => `<span class="ln">${line || '&nbsp;'}</span>`)
        .join('\n');
}

function showLoginPrompt(id: string, container: HTMLElement): void {
    const next = encodeURIComponent(`/paste/${id}${window.location.search}`);
    container.innerHTML = `
        <div class="login-prompt">
            <h3>This is a private paste</h3>
            <p>Log in to view it.</p>
            <a class="btn btn-primary" href="/login?next=${next}">Log In</a>
        </div>
    `;
}
